'use client';
import PropTypes from 'prop-types';
import React from 'react';
import {
  beginDomSyncRetry,
  failDomSyncRetry,
  getLastDomSyncContext,
  getStashedComponentStack,
  getStashedDomSyncError,
  hasDomSyncBeenReported,
  hasDomSyncRetried,
  hasDomSyncTriggered,
  installDomSyncProbe,
  isDomSyncError,
  isDomSyncRetrying,
  markDomSyncReported,
  noteDomSyncRetrySucceeded,
  stashDomSyncComponentStack,
  stashDomSyncError,
} from './domSyncError';
import { ErrorPage } from '.';

// Installed at load so the nodes are captured before the boundary runs.
installDomSyncProbe();

const reportDomSync = reporter => {
  if (hasDomSyncBeenReported()) return;
  const error = getStashedDomSyncError();
  if (!error) return;

  markDomSyncReported();
  reporter.error(error, {
    contexts: {
      componentStack: getStashedComponentStack(),
      // parent / child / reference nodes from the failed insertBefore or removeChild
      domSync: getLastDomSyncContext(),
    },
  });
};

const leaveErrorScreen = event => {
  const link = event.target.closest?.('a[href]');
  if (!link) return;
  event.preventDefault();
  window.location.assign(link.href);
};

const DomSyncRetry = ({ reporter, onRetry }) => {
  React.useEffect(() => {
    // React 19 can skip componentDidCatch on its synchronous retry.
    reportDomSync(reporter);
    onRetry();
  }, [reporter, onRetry]);

  return null;
};

DomSyncRetry.propTypes = {
  reporter: PropTypes.object.isRequired,
  onRetry: PropTypes.func.isRequired,
};

const DomSyncFallback = ({ reporter }) => {
  React.useEffect(() => {
    reportDomSync(reporter);
  }, [reporter]);

  return (
    <div onClick={leaveErrorScreen}>
      <ErrorPage statusCode={500} />
    </div>
  );
};

DomSyncFallback.propTypes = {
  reporter: PropTypes.object.isRequired,
};

export class ErrorBoundary extends React.Component {
  static propTypes = {
    children: PropTypes.node.isRequired,
    reporter: PropTypes.object.isRequired,
  };

  state = { hasError: false, allowRemount: false };

  retryPage = () => {
    if (this.state.allowRemount || hasDomSyncTriggered()) return;
    this.setState({
      allowRemount: true,
      hasError: false,
      domSyncError: false,
    });
  };

  static getDerivedStateFromError(error) {
    if (isDomSyncError(error)) {
      stashDomSyncError(error);
      // The first miss mounts the page again. A throw during that remount,
      // or any later DOM-sync error in this document, stays on Whoops.
      if (isDomSyncRetrying() || hasDomSyncRetried() || hasDomSyncTriggered()) {
        failDomSyncRetry();
        return { hasError: true, domSyncError: true, allowRemount: false };
      }
      beginDomSyncRetry();
      return { hasError: true, domSyncError: false, allowRemount: false };
    }
    return { hasError: true, domSyncError: false };
  }

  componentDidCatch(error, errorInfo) {
    if (isDomSyncError(error)) {
      stashDomSyncComponentStack(errorInfo?.componentStack);
      reportDomSync(this.props.reporter);
      return;
    }

    this.props.reporter.error(error, { contexts: errorInfo });
  }

  componentDidUpdate() {
    if (
      this.state.allowRemount &&
      !this.state.hasError &&
      !this.state.domSyncError &&
      isDomSyncRetrying()
    ) {
      noteDomSyncRetrySucceeded();
    }
  }

  render() {
    if (hasDomSyncTriggered() || this.state.domSyncError) {
      return <DomSyncFallback reporter={this.props.reporter} />;
    }
    if (isDomSyncRetrying() && !this.state.allowRemount) {
      return (
        <DomSyncRetry reporter={this.props.reporter} onRetry={this.retryPage} />
      );
    }
    if (this.state.hasError) {
      return <ErrorPage statusCode={500} />;
    }
    return this.props.children;
  }
}
